import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, TextField } from "@mui/material";
import { useFormikContext } from "formik";
import React, { useState } from "react";

import MDTypography from "@/components/atoms/MDTypography";
import { TableCell, TableRow } from "@/components/molecules/Table/styled";
import { OrderFormData } from "@/components/screens/OrderFormPage/types";
import { formatCurrency } from "@/helpers/currency-helpers";
import { formatVariationsLabel } from "@/helpers/variation-helpers";
import { IOrderItem } from "@/interfaces/order.interfaces";
import { ItemDataTabMessages } from "@/messages/order-form-page/item-data-tab.messages";

import { applyCampaignPriceToItem, resolveEligibleCampaign } from "../../form-data";

interface Props {
  item: IOrderItem;
  index: number;
  messages: ItemDataTabMessages;
}

export function ItemTableRow({ item, index, messages }: Props) {
  const [quantityError, setQuantityError] = useState<string | null>(null);
  const { values, errors, setFieldValue } = useFormikContext<OrderFormData>();
  const { removedItems, items } = values;

  const updateItemQuantity = async (skuCode: string, newQuantity: number) => {
    const updatedItems = await Promise.all(
      values.items.map(async (currentItem) => {
        if (currentItem.skuCode !== skuCode || currentItem.lineType === 2) {
          return currentItem;
        }

        const campaign = await resolveEligibleCampaign(
          currentItem.campaignId ?? values.campaignId,
          currentItem.discountCouponCode || values.discountCouponCode,
          values.contactId
        );

        return applyCampaignPriceToItem({ ...currentItem, quantity: newQuantity }, campaign);
      })
    );

    setFieldValue("items", updatedItems);
  };

  const updateItemComment = (skuCode: string, comment: string) => {
    const updatedItems = values.items.map((item) => {
      if (item.skuCode === skuCode) {
        return { ...item, userComment: comment };
      }
      return item;
    });

    setFieldValue("items", updatedItems);
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newQuantity = parseFloat(event.target.value);

    if (newQuantity === 0) {
      setQuantityError("O valor não pode ser 0.");
    } else {
      updateItemQuantity(item.skuCode, newQuantity);
    }
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const comment = event.target.value;
    updateItemComment(item.skuCode, comment);
  };

  const handleRemoveItem = () => {
    if (item.id) {
      setFieldValue("removedItems", [...removedItems, item]);
      const newItems = items.filter((payment) => payment.id !== item.id);
      setFieldValue("items", newItems);
    } else {
      const newItems = items.filter((payment) => payment.skuCode !== String(item.skuCode));
      setFieldValue("items", newItems);
    }
  };

  const itemError = typeof errors.items === "object" ? errors.items[index] : undefined;
  const itemQuantityError = itemError ? "Erro específico do item" : quantityError;
  const grossUnitPrice = Number(item.grossUnitPrice || item.listPrice || item.netUnitPrice);
  const netUnitPrice = Number(item.netUnitPrice);
  const grossLineAmount = Number(item.grossLineAmount || item.netLineAmount);
  const netLineAmount = Number(item.netLineAmount);

  return (
    <TableRow index={index}>
      <TableCell>{item.skuCode}</TableCell>
      <TableCell>{item.description}</TableCell>
      <TableCell>{formatOrderItemVariation(item)}</TableCell>
      <TableCell align="right" sx={{ width: "1%", whiteSpace: "nowrap" }}>
        <TextField
          variant="standard"
          value={item.quantity}
          onChange={handleQuantityChange}
          type="number"
          error={!!quantityError || !!errors.items}
          helperText={itemQuantityError || ""}
          disabled={values.disabledButton}
          sx={{
            width: "5rem",
            "& input": { textAlign: "right" },
            "& .MuiFormHelperText-root": { textAlign: "right" },
          }}
          slotProps={{
            htmlInput: { min: 1 },
          }}
        />
      </TableCell>
      <TableCell align="right" sx={{ width: "1%", whiteSpace: "nowrap" }}>
        <DiscountedPrice original={grossUnitPrice} discounted={netUnitPrice} />
      </TableCell>
      <TableCell align="right" sx={{ width: "1%", whiteSpace: "nowrap" }}>
        <DiscountedPrice original={grossLineAmount} discounted={netLineAmount} />
      </TableCell>
      <TableCell>
        {item.needComment ? (
          <TextField
            variant="standard"
            value={item.userComment || ""}
            onChange={handleCommentChange}
            placeholder={messages.userCommentPlaceholder}
            multiline
            maxRows={3}
            disabled={values.disabledButton}
            required
            error={item.needComment && !item.userComment}
            helperText={item.needComment && !item.userComment ? "Campo obrigatório" : ""}
            slotProps={{
              htmlInput: { maxLength: 500 },
            }}
          />
        ) : (
          <span>-</span>
        )}
      </TableCell>
      <TableCell>
        <IconButton
          onClick={() => handleRemoveItem()}
          disabled={values.disabledButton}
          aria-label="delete item"
        >
          <DeleteIcon fontSize="medium" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function formatOrderItemVariation(item: IOrderItem): string {
  const fromJson = formatVariationsLabel(item.variations);
  if (fromJson && fromJson !== "-") return fromJson;

  const fallbackFields = item as IOrderItem & Record<string, string | number | null | undefined>;
  const fallback = [1, 2, 3, 4, 5]
    .map((index) => {
      const name = String(fallbackFields[`variationCode0${index}`] ?? "").trim();
      const value = String(fallbackFields[`variationValue0${index}`] ?? "").trim();
      if (!name && !value) return null;
      if (name && value) return `${name}: ${value}`;
      return name || value;
    })
    .filter(Boolean);

  return fallback.length > 0 ? fallback.join(", ") : "-";
}

function DiscountedPrice({ original, discounted }: { original: number; discounted: number }) {
  const hasDiscount = original > discounted + 0.009;

  if (!hasDiscount) {
    return <>{formatCurrency(discounted)}</>;
  }

  return (
    <>
      <MDTypography variant="caption" color="text" sx={{ textDecoration: "line-through", display: "block" }}>
        {formatCurrency(original)}
      </MDTypography>
      <MDTypography variant="button" color="success" fontWeight="bold">
        {formatCurrency(discounted)}
      </MDTypography>
    </>
  );
}

